import FJSprite from './FJSprite';
import Animation, { ANIMATION_NAME, DEFAULT_ANIMATION_DURATION } from './motion/FJStaticAnimation';
import { ANIMATION_NAME as TEXT_ANIMATION_NAME } from './motion/FJTextAnimation';
import FJViewConstant from '../constants/FJViewConstant';
import FJArchiveUtil from '../util/FJArchiveUtil';
import FJMathUtil from '../util/FJMathUtil';
import FJAnimationTimelineData from './motion/FJAnimationTimelineData';
import FJMotionData from './motion/FJMotionData';
import FJMotionUtil from '../util/FJMotionUtil';
import { isObject } from 'lodash-es';
import FJCoreConfig from './FJCoreConfig';

class FJAnimationSprite extends FJSprite {
    constructor(props) {
        super(props);
        this._animation = null;
        /**
         * @desc 整个动画持续时间，包含delay与endDelay,单位为秒
         * @type {*|number}
         */
        this._duration = props.duration || FJCoreConfig.defaultSceneDuration;
        /**
         * @desc 动画开始执行的延迟，送动画开始播放时开始计时，单位为秒
         * @type {*|number}
         */
        this._delay = props.delay || 0;
        /**
         * @desc 结束延迟的时间，单位为秒
         * @type {*|number}
         */
        this._endDelay = props.endDelay || 0; //动画相对于故事板的提前
        /**
         * @desc 进场动画的名字
         * @type {string}
         */
        this._animationInName = props.animationInName || 'None';
        /**
         * @desc 进场动画的循环动画的名字
         * @type {string}
         */
        this._animationLoopName = props.animationLoopName || 'None';
        /**
         * @desc 退场动画的名字
         * @type {string}
         */
        this._animationOutName = props.animationOutName || 'None';

        /**
         * @description 进场动画的时长（如果是整体的进出场动画，这个值是进场动画的时间）
         * @type {number}
         */
        this._enterAnimationTime = props.enterAnimationTime || 0;
        /**
         * @description 循环动画的时长(这个值是循环动画执行一个循环需要的时间)
         * @type {number}
         */
        this._loopAnimationTime = props.loopAnimationTime || 1;
        /**
         * @description 出场动画的时长
         * @type {number}
         */
        this._exitAnimationTime = props.exitAnimationTime || 0;

        this._animationTextColor = props.animationTextColor || '#ffffff';
        this._animationBackgroundColor = props.animationBackgroundColor || '#000000';

        this._customAnimation = null;
        this._customAnimationTarget = document.createElement('div');
        this._customAnimationInfo = props.customAnimationInfo || {};
    }

    set customAnimationInfo(customAnimationInfo) {
        this._customAnimationInfo = customAnimationInfo;
    }

    get customAnimationInfo() {
        return this._customAnimationInfo;
    }

    set animationTextColor(animationTextColor) {
        this._animationTextColor = animationTextColor;
    }

    get animationTextColor() {
        return this._animationTextColor;
    }

    set animationBackgroundColor(animationBackgroundColor) {
        this._animationBackgroundColor = animationBackgroundColor;
    }

    get animationBackgroundColor() {
        return this._animationBackgroundColor;
    }

    set enterAnimationTime(enterAnimationTime) {
        // TODO 在一些操作后，会导致时长为负的情况，如果此时使用了退场动画，则会无法显示该元素
        this._enterAnimationTime = enterAnimationTime < 0 ? 0 : enterAnimationTime;
    }

    get enterAnimationTime() {
        return this._enterAnimationTime;
    }

    set exitAnimationTime(exitAnimationTime) {
        // TODO 在一些操作后，会导致时长为负的情况，如果此时使用了退场动画，则会无法显示该元素
        this._exitAnimationTime = exitAnimationTime < 0 ? 0 : exitAnimationTime;
    }

    get exitAnimationTime() {
        return this._exitAnimationTime;
    }
    set loopAnimationTime(loopAnimationTime) {
        this._loopAnimationTime = loopAnimationTime < 0 ? 0 : loopAnimationTime;
    }
    get loopAnimationTime() {
        return this._loopAnimationTime;
    }

    toJSON() {
        return {
            ...super.toJSON(),
            animationInName: this._animationInName,
            animationLoopName: this._animationLoopName,
            animationOutName: this._animationOutName,
            duration: this._duration,
            delay: this._delay,
            endDelay: this._endDelay,
            enterAnimationTime: this.enterAnimationTime,
            exitAnimationTime: this.exitAnimationTime,
            loopAnimationTime: this.loopAnimationTime,

            animationTextColor: this.animationTextColor,
            animationBackgroundColor: this.animationBackgroundColor,

            customAnimationInfo: this.customAnimationInfo,
        };
    }

    fromJSON(data) {
        super.fromJSON(data);
        this._animationInName = data.animationInName;
        this._animationLoopName = data.animationLoopName;
        this._animationOutName = data.animationOutName;
        this._duration = data.duration;
        this._delay = data.delay;
        this._endDelay = data.endDelay;
        this._enterAnimationTime = data.enterAnimationTime;
        this._exitAnimationTime = data.exitAnimationTime;
        this._loopAnimationTime = data.loopAnimationTime;

        this.animationTextColor = data.animationTextColor;
        this.animationBackgroundColor = data.animationBackgroundColor;

        this.customAnimationInfo = data.customAnimationInfo;
    }

    encode() {
        let data = super.encode();
        data.animationInName = this._animationInName;
        data.animationLoopName = this._animationLoopName;
        data.animationOutName = this._animationOutName;
        data.duration = this._duration;
        data.delay = this._delay;
        data.endDelay = this._endDelay;
        data.enterAnimationTime = this.enterAnimationTime;
        data.exitAnimationTime = this.exitAnimationTime;
        data.loopAnimationTime = this.loopAnimationTime;
        data.animationTextColor = this.animationTextColor;
        data.animationBackgroundColor = this.animationBackgroundColor;

        data.customAnimationInfo = this.customAnimationInfo;
        return data;
    }

    decode(data) {
        super.decode(data);
        this._animationInName = data.animationInName;
        this._animationLoopName = data.animationLoopName;
        this._animationOutName = data.animationOutName;
        this._duration = Math.max(0, data.duration);
        this._delay = Math.max(0, data.delay);
        this._endDelay = Math.max(0, data.endDelay);
        this._enterAnimationTime = data.enterAnimationTime;
        this._exitAnimationTime = data.exitAnimationTime;
        this._loopAnimationTime = data.loopAnimationTime;
        this.animationTextColor = data.animationTextColor;
        this.animationBackgroundColor = data.animationBackgroundColor;

        this.customAnimationInfo = data.customAnimationInfo;

        if (!data.ignoreAnimation) this._createAnimation();
    }

    clone(object) {
        if (typeof object === 'undefined') object = new FJAnimationSprite();
        super.clone(object);
        object._isTextEditMode = this._isTextEditMode;
        object._animationInName = this._animationInName;
        object._animationLoopName = this._animationLoopName;
        object._animationOutName = this._animationOutName;
        object.enterAnimationTime = this.enterAnimationTime;
        object.exitAnimationTime = this.exitAnimationTime;
        object.loopAnimationTime = this.loopAnimationTime;
        object.animationTextColor = this.animationTextColor;
        object.animationBackgroundColor = this.animationBackgroundColor;
        object.setTimeData(this._duration, this._delay, this._endDelay);
        return object;
    }

    /**
     * @desc 进行动画的元素
     * @return {null}
     * @private
     */
    _animationTarget = () => {
        return null;
    };

    /**
     * @desc 动画元素的父节点
     * @return {null}
     * @private
     */
    _animationTargetContainer = () => {
        return null;
    };

    getParentElement() {
        if (this._groupParent) return this._groupParent.layer();
        else {
            if (!this._animationTargetContainer()) return null;
            return this._animationTargetContainer().parentNode;
        }
    }

    //动画相对于故事板的裁剪时间：duration, delay, endDelay
    //duration指的是整个故事板的时间
    /**
     * @description 设置时长
     * @param duration
     * @param delay
     * @param endDelay
     * @param options
     * @param options.shouldToCover
     * @param options.updateAnimation
     */
    setTimeData(duration, delay, endDelay, options = {}) {
        const { shouldToCover = true, updateAnimation = true } = options;
        if (typeof duration !== 'undefined') this._duration = duration;
        if (typeof delay !== 'undefined') {
            if (delay < 1e-6) delay = 0;
            this._delay = delay;
        }
        if (typeof endDelay !== 'undefined') {
            if (endDelay < 1e-6) endDelay = 0;
            this._endDelay = endDelay;
        }

        if (updateAnimation) this._createAnimation();
        if (shouldToCover) this.goToCoverFrame();
    }

    /**
     * @desc 获取动画播放的时长及延迟
     * @param useSpeed - 是否应用速率
     * @return {{duration: (*|number), delay: (*|number), endDelay: (*|number)}}
     */
    getTimeData(useSpeed = false) {
        return {
            duration: this._duration,
            delay: this._delay,
            endDelay: this._endDelay,
        };
    }

    //返回动画自身的各个持续时间：showDuration, hideDuration, staticDuration, loopDuration
    //不会真正改变this._showDuration, this._hideDuration. 以便下次裁剪后用以计算
    /**
     * @desc 获取动画在各个时段的时间
     * @return {{hideDuration: number, staticDuration: number, showDuration: number, loopDuration: number }}
     */
    getDetailTime() {
        let showDuration = 0,
            hideDuration = 0,
            loopDuration = 0;
        if (this._animationInName !== 'None') {
            showDuration = this._enterAnimationTime;
        }
        if (this._animationOutName !== 'None') {
            hideDuration = this._exitAnimationTime;
        }

        let staticDuration = 0;
        let { duration, delay, endDelay } = this.getTimeData(true);
        let currentDuration = duration - delay - endDelay;
        if (currentDuration >= showDuration + hideDuration) {
            staticDuration = currentDuration - (showDuration + hideDuration);
        } else {
            showDuration = hideDuration = currentDuration / 2;
        }
        if (this._animationLoopName !== 'None') {
            loopDuration = staticDuration;
            staticDuration = 0;
        }
        return {
            showDuration: showDuration,
            loopDuration: loopDuration,
            hideDuration: hideDuration,
            staticDuration: staticDuration,
        };
    }

    playLoopAnimation(callback) {
        if (this._animationTarget() && this.getParentElement() && this._animation) {
            if (this._animationLoopName === 'None') {
                if (callback) callback();
            } else {
                let { duration, delay, endDelay } = this.getTimeData(true);
                let { showDuration } = this.getDetailTime();
                let time = delay + showDuration;
                this.seek(time);
                let playDuration = (duration - delay - endDelay) * 1000;
                // 整体循环动画预览播放整个素材时长
                if (playDuration !== 0) {
                    this.play(time, true);
                    this._setStopPlayTimingTrigger(Math.min(playDuration, 3000), callback);
                }
            }
        }
    }

    playShowAnimation(callback) {
        if (this._animationTarget() && this.getParentElement() && this._animation) {
            if (this._animationInName === 'None') {
                if (callback) callback();
            } else {
                let { delay } = this.getTimeData(true);
                this.seek(delay);
                // 整体循环动画预览播放整个素材时长

                let playDuration = this.getDetailTime().showDuration * 1000;

                if (playDuration !== 0) {
                    this.play(delay, true);
                    this._setStopPlayTimingTrigger(playDuration, callback);
                }
            }
        }
    }

    playHideAnimation(callback) {
        if (this._animationTarget() && this.getParentElement()) {
            if (this._animationOutName === 'None') {
                if (callback) callback();
            } else {
                let timeData = this.getDetailTime();
                let { showDuration, hideDuration, staticDuration, loopDuration } = timeData;
                let time = this._delay + showDuration + staticDuration + loopDuration;
                this.seek(time);
                if (hideDuration !== 0) {
                    this.play(time, true);
                    this._setStopPlayTimingTrigger(hideDuration * 1000, callback);
                }
            }
        }
    }

    /**
     * @desc 直接跳转到进场动画结束的帧
     */
    goToCoverFrame() {
        if (this._animation && this._animation.isActive) {
            if (this._animation.isCompleted()) {
                this._animation.reset();
            }
            if (!this._animation.isPaused()) {
                this._animation.pause();
            }

            let animationInName =
                ANIMATION_NAME[this._animationInName] || TEXT_ANIMATION_NAME[this._animationInName]
                    ? this._animationInName
                    : 'None';
            let animationLoopName =
                ANIMATION_NAME[this._animationLoopName] || TEXT_ANIMATION_NAME[this._animationLoopName]
                    ? this._animationLoopName
                    : 'None';
            let animationOutName =
                ANIMATION_NAME[this._animationOutName] || TEXT_ANIMATION_NAME[this._animationOutName]
                    ? this._animationOutName
                    : 'None';

            let { duration, delay, endDelay } = this.getTimeData(true);
            let { showDuration, loopDuration } = this.getDetailTime();

            if (animationInName === 'None' && animationLoopName === 'None') {
                // 无动画
                if (this._isValidMoveAnimationData()) {
                    this.coverFrameSeek(delay * 1000);
                } else {
                    this.coverFrameSeek(delay * 1000 + DEFAULT_ANIMATION_DURATION);
                }
            } else if (animationLoopName === 'None') {
                // 入场动画
                let coverFrameTime = (showDuration + delay) * 1000;
                if (animationOutName === 'None' && coverFrameTime >= (duration - endDelay) * 1000)
                    coverFrameTime -= DEFAULT_ANIMATION_DURATION;
                this.coverFrameSeek(coverFrameTime);
            } else if (animationInName === 'None') {
                // 循环动画
                // TODO： 部分循环动画无法找到coverFrameTime，所以需要特殊处理
                const animeData = FJMotionData[this._animationLoopName];
                if (animeData && animeData.methods && animeData.methods.goToCoverFrame) {
                    animeData.methods.goToCoverFrame([this._animationTarget()]);
                } else {
                    let coverFrameTime = Animation.getCoverFrameTime(animationLoopName, this._loopAnimationTime);
                    this.coverFrameSeek(coverFrameTime * 1000);
                }
            } else {
                // 入场动画 + 循环动画
                // TODO: 部分循环动画无法找到coverFrameTime，所以需要特殊处理
                const animeData = FJMotionData[this._animationLoopName];
                if (animeData && animeData.methods && animeData.methods.goToCoverFrame) {
                    animeData.methods.goToCoverFrame([this._animationTarget()]);
                    return;
                }

                let animeCoverFrameTime = Animation.getCoverFrameTime(animationLoopName, this._loopAnimationTime);
                let coverFrameTime = (showDuration + delay + animeCoverFrameTime) * 1000;
                // 循环动画的时长不足以到coverFrameTime，则需要seek到入场动画最后一帧
                if (coverFrameTime > (showDuration + loopDuration + delay) * 1000) {
                    coverFrameTime = (showDuration + delay) * 1000;
                }
                if (animationOutName === 'None' && coverFrameTime >= (duration - endDelay) * 1000)
                    coverFrameTime -= DEFAULT_ANIMATION_DURATION;
                this.coverFrameSeek(coverFrameTime);
            }
        }

        if (this._customAnimation) {
            const { delay } = this.getTimeData(true);
            this._customAnimation.seek(delay * 1000);
        }
    }
    /**
     * @param {*} time 毫秒
     */
    coverFrameSeek(time) {
        // time = Math.floor(time * 100) / 100;
        this._animation && this._animation.seek(time);
    }

    _createPlayRequestAnimation(callback) {
        this._playRequestTimer = window.requestAnimationFrame(callback);
    }
    _cancelPlayRequestAnimation() {
        if (this._playRequestTimer) {
            window.cancelAnimationFrame(this._playRequestTimer);
            this._playRequestTimer = null;
        }
    }

    /**
     * @desc
     * @param currentTime - 单位s
     */
    play(currentTime = 0) {
        this._customPlay(currentTime);
        if (!this._animation || !this._animationTarget()) {
            return;
        }
        this._clearTimingTrigger();
        // if (this._animation.isLoop() && typeof currentTime !== 'undefined' && currentTime < this._delay) {
        //     let sTime;
        //     let __play = time => {
        //         if (!sTime) sTime = time;
        //         if (currentTime + (time - sTime) / 1000 >= this._delay) {
        //             this._animation.play();
        //             this._cancelPlayRequestAnimation();
        //         } else {
        //             this._createPlayRequestAnimation(__play);
        //         }
        //     };
        //     this._createPlayRequestAnimation(__play);
        // } else {
        this._animation.play();
        // }
    }

    _customPlay(currentTime) {
        if (!this._customAnimation || !this._isValidMoveAnimationData()) {
            return;
        }
        this._clearTimingTrigger();
        this._customAnimation.play(currentTime);
    }

    /**
     * @desc 设置时间，单位为秒
     * @param time
     */
    seek(time, options = {}) {
        // 重置动画到第一帧，+0.01是为了强制更新动画，如果直接seek到delay不会更新
        const startTime = this.getTimeData().delay;
        if (time <= startTime) {
            const t = startTime * 1000 + 10;
            this._animation && this._animation.seek(t, options);
        }

        this._customSeek(time);
        if (!this._animation || !this._animationTarget()) {
            return;
        }

        this._clearTimingTrigger();
        if (this._animation.isCompleted()) {
            this._animation.reset(); // 不要用this.reset()方法，会导致本来只是这里的animation需要reset，但是如果子类重载了reset方法后，会导致一些副作用
        }
        if (!this._animation.isPaused()) {
            this._animation.pause(); // 不要用this.pause()方法，会导致本来只是这里的animation需要pause，但是如果子类重载了pause方法后，会导致一些副作用
        }

        // if (this._animation.isLoop()) {
        //     // 还未开始播放则seek到0
        //     if (time <= this._delay) {
        //         this._animation.seek(this._delay * 1000);
        //     } else {
        //         // let duration = this._animation.getDuration() / 1000;
        //         // let t = time - this._delay; // 相对于动画实际开始播放的时间
        //         // let t1 = t >= 0 ? t % duration : time;
        //         this._animation.seek(time * 1000);
        //     }
        // } else {
        this._animation.seek(time * 1000, options);
        // }
    }

    _customSeek(time) {
        if (!this._customAnimation || !this._isValidMoveAnimationData()) {
            return;
        }

        this._customAnimation.seek(time);
    }

    stop() {
        this._cancelPlayRequestAnimation();
        this._clearTimingTrigger();
        if (this._animation) {
            this._animation.pause();
            this._animation.reset();
        }
        if (this._customAnimation) {
            this._customAnimation.pause();
        }
    }

    pause() {
        this._cancelPlayRequestAnimation();
        this._clearTimingTrigger();
        if (this._animation) {
            this._animation.pause();
        }
        if (this._customAnimation) {
            this._customAnimation.pause();
        }
    }

    reset() {
        if (this._animation) {
            this._animation.reset();
        }
    }

    /**
     * @desc 返回做move动画时的上下左右的偏移
     * @return {{top: number, left: number, bottom: number, right: number}}
     * @private
     */
    _getMoveAnimationData() {
        let relativeRect = this._relativeRect;
        const leaveDistance = this._getMoveAnimationLength();
        if (this.groupParent()) {
            let groupRelativeRect = this.groupParent().relativeRect();

            return {
                left: leaveDistance.left * this._flipX,
                right: leaveDistance.right * this._flipX,
                top: leaveDistance.top * this._flipY,
                bottom: leaveDistance.bottom * this._flipY,
                width: relativeRect.width * groupRelativeRect.width * this._flipX,
                height: relativeRect.height * groupRelativeRect.height * this._flipY,
            };
        } else {
            return {
                left: leaveDistance.left * this._flipX,
                right: leaveDistance.right * this._flipX,
                top: leaveDistance.top * this._flipY,
                bottom: leaveDistance.bottom * this._flipY,
                width: relativeRect.width * this._flipX,
                height: relativeRect.height * this._flipY,
            };
        }
    }

    /**
     * @desc 获得工程画布的实际大小
     */
    _getProjectCanvasSize() {
        let viewSize = FJArchiveUtil.getCurrentPreviewBoardViewSize();
        if (viewSize.width !== 0 && viewSize.height !== 0) {
            return viewSize;
        } else {
            return FJArchiveUtil.getPageSize();
        }
    }

    _getMoveAnimationLength() {
        let viewSize = this._getProjectCanvasSize();
        const bigRect = {
            x: 0,
            y: 0,
            width: viewSize.width,
            height: viewSize.height,
        };

        let rect = this.getRect(viewSize);

        if (this.groupParent()) {
            let group = this.groupParent();
            let groupRelativeRect = group.relativeRect();
            let groupRectSize = group.getRect(viewSize);
            rect = this.getRect(groupRectSize);

            // getRect方法计算出来的rect位置需要加上打组元素的位置
            rect.x += groupRelativeRect.x * viewSize.width;
            rect.y += groupRelativeRect.y * viewSize.height;
        }

        let leaveDistance = FJMathUtil.getLeaveBigRectDistance(bigRect, rect, this._angle);

        // 转换成相对于自身的比例
        leaveDistance.top = leaveDistance.top / rect.height;
        leaveDistance.bottom = leaveDistance.bottom / rect.height;
        leaveDistance.left = leaveDistance.left / rect.width;
        leaveDistance.right = leaveDistance.right / rect.width;

        return leaveDistance;
    }

    _createAnimation() {
        let target = this._animationTarget();
        if (!target) return;

        this._destroyAnimation();
        let targetContainer = this._animationTargetContainer();

        let animationInName = ANIMATION_NAME[this._animationInName] ? this._animationInName : 'None';
        let animationLoopName = ANIMATION_NAME[this._animationLoopName] ? this._animationLoopName : 'None';
        let animationOutName = ANIMATION_NAME[this._animationOutName] ? this._animationOutName : 'None';

        if (animationInName !== 'None' || animationOutName !== 'None' || animationLoopName !== 'None') {
            const { duration, delay, endDelay } = this.getTimeData(true);
            const { showDuration, staticDuration, hideDuration, loopDuration } = this.getDetailTime();
            const animationDuration = duration - delay - endDelay;
            // startTime和endTime表示在时间线上有意义的时间范围： 进场，退场，静态
            // animationTimes数组里存放的是做动画的时间范围
            // TODO timeLineTimeData中的时间单位都是ms
            const timeLineTimeData = new FJAnimationTimelineData({
                startTime: delay * 1000,
                endTime: (delay + animationDuration) * 1000,
            });

            const useInAnimation = animationInName !== 'None' && showDuration > 0;
            const useOutAnimation = animationOutName !== 'None' && hideDuration > 0;
            const useLoopAnimation = animationLoopName !== 'None' && loopDuration > 0;

            // TODO 添加进场动画的时间
            if (useInAnimation) {
                const endTime = delay + showDuration;
                timeLineTimeData.addTimeData({
                    startTime: delay * 1000,
                    endTime: endTime * 1000,
                    animationName: animationInName,
                    duration: showDuration * 1000,
                });
            }
            // TODO 添加退场动画的时间
            if (useOutAnimation) {
                let startTime = (delay + showDuration + staticDuration + loopDuration) * 1000;
                if (startTime < delay * 1000 + DEFAULT_ANIMATION_DURATION) {
                    startTime = delay * 1000 + DEFAULT_ANIMATION_DURATION;
                }
                const maxHideDuration = animationDuration - DEFAULT_ANIMATION_DURATION / 1000;
                timeLineTimeData.addTimeData({
                    startTime: startTime,
                    endTime: (delay + showDuration + staticDuration + loopDuration + hideDuration) * 1000,
                    animationName: animationOutName,
                    duration: Math.min(hideDuration, maxHideDuration) * 1000,
                });
            }
            if (useLoopAnimation) {
                let startTime = (delay + showDuration + staticDuration) * 1000;
                let endTime = startTime + loopDuration * 1000;
                timeLineTimeData.addTimeData({
                    startTime,
                    endTime,
                    animationName: animationLoopName,
                    duration: this._loopAnimationTime * 1000,
                });
            }
            // 仅用于做入出场动画
            if (useInAnimation || useOutAnimation || useLoopAnimation) {
                this._animation = new Animation({
                    animationContainer: targetContainer,
                    animationTargets: [target],
                    animationInName: animationInName,
                    animationLoopName: animationLoopName,
                    animationOutName: animationOutName,
                    timeLineTimeData: timeLineTimeData,
                    textGetAnimationByTimeFun: this._getAnimationNameByTime
                        ? this._getAnimationNameByTime.bind(this)
                        : null,
                });
                const moveData = this._getMoveAnimationData();

                timeLineTimeData.animationTimes.forEach(data => {
                    const { animationName, duration, startTime, endTime } = data;
                    this._animation.addAnimation([target], animationName, duration, 0, startTime, endTime, {
                        moveData: moveData,
                    });
                });
            }
            this._animation && this._animation.reset();
        } else {
            // TODO 这里不管有没有动画都要创建一个，因为很多地方都要判断this._animation有没有，没有就不会调用后续的方法，所以这里要创建一个
            this._animation = new Animation({
                animationInName: animationInName,
                animationOutName: animationOutName,
                isActive: false,
                textGetAnimationByTimeFun: this._getAnimationNameByTime
                    ? this._getAnimationNameByTime.bind(this)
                    : null,
            });
        }
    }

    /**
     * @desc 更新动画
     */
    updateAnimation() {
        this._createAnimation();
        this.goToCoverFrame();
    }

    /**
     * @desc 销毁动画
     */
    _destroyAnimation() {
        if (this._animation) {
            let target = this._animationTarget();
            let targets = null;
            if (target) targets = [target];
            this._animation.destroy && this._animation.destroy(targets);
            this._animation = null;
            // TODO 部分动画会添加 overflow属性，所以这里需要清除
            this._animationTargetContainer().style.overflow = 'visible';
        }
    }
    /**
     * @desc
     * @private
     */
    _clearTimingTrigger() {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
            if (this._updateVideoVolume) {
                this._updateVideoVolume();
            }
            if (this.getType() === FJViewConstant.GRAPH_GROUP) {
                this.getAllElements().forEach(ele => {
                    if (ele.getType() === FJViewConstant.PIP_ELEMENT_VIDEO) {
                        // todo 之后优化写法
                        ele._updateVideoVolume();
                    }
                });
            }
        }
    }
    /**
     * @desc 设置结束播放定时器
     * @param time
     * @param callback
     */
    _setStopPlayTimingTrigger(time, callback) {
        this._clearTimingTrigger();

        if (this.getType() === FJViewConstant.GRAPH_GROUP) {
            this.getAllElements().forEach(ele => {
                if (ele.getType() === FJViewConstant.PIP_ELEMENT_VIDEO) {
                    // TODO 之后优化写法
                    if (ele._video) ele._video.volume = 0;
                }

                if (ele.getType() === FJViewConstant.GRAPH_TEXT_MASK) {
                    const backgroundElement = ele.getBackgroundElement();
                    if (backgroundElement._video) backgroundElement._video.volume = 0;
                }
            });
        }

        if (this.getType() === FJViewConstant.PIP_ELEMENT_VIDEO) {
            if (this._video) this._video.volume = 0;
        }

        if (this.getType() === FJViewConstant.GRAPH_TEXT_MASK) {
            const backgroundElement = this.getBackgroundElement();
            if (backgroundElement._video) backgroundElement._video.volume = 0;
        }

        this._timer = setTimeout(() => {
            this.pause();
            this._clearTimingTrigger();
            callback && callback();
        }, time);
    }

    getAnimationCurrentTime() {
        if (!this._animation) return -1;
        return this._animation.getCurrentTime();
    }

    /**
     * @desc 是否这个类型是否具有动画属性，如果子类集成此类，则可以通过这个方法判断
     * @return {boolean}
     */
    hasAnimation() {
        return true;
    }

    /**
     * @desc 是否没有用任何动画
     * @return {boolean}
     */
    isNoneAnimation() {
        return this._animationInName.toLowerCase() === 'none' && this._animationOutName.toLowerCase() === 'none';
    }

    /**
     * @desc 设置进场出场动画
     * @param {{animationInName: string, animationOutName: string}} data - 进场退场动画的名字
     */
    setAnimationName(data) {
        if (this._timer) {
            this._clearTimingTrigger();
            this.pause();
        }

        if (typeof data.animationInName !== 'undefined') this._animationInName = data.animationInName;
        if (typeof data.animationLoopName !== 'undefined') this._animationLoopName = data.animationLoopName;
        if (typeof data.animationOutName !== 'undefined') this._animationOutName = data.animationOutName;

        this._createAnimation();
    }

    /**
     * @desc 获取进场退场动画的名字
     * @return {{animationInName: string, animationOutName: string}}
     */
    getAnimationName() {
        return {
            animationInName: this._animationInName,
            animationLoopName: this._animationLoopName,
            animationOutName: this._animationOutName,
        };
    }

    /**
     * @description 设置进出场动画的时间
     * @param data
     */
    setAnimationTime(data) {
        if (typeof data.enterAnimationTime !== 'undefined') this.enterAnimationTime = data.enterAnimationTime;
        if (typeof data.exitAnimationTime !== 'undefined') this.exitAnimationTime = data.exitAnimationTime;
        if (typeof data.loopAnimationTime !== 'undefined') this.loopAnimationTime = data.loopAnimationTime;

        this._createAnimation();

        // 考虑是否需要
        this.goToCoverFrame();
        this.updateProjectData();
    }

    /**
     * @description1 获取进出场动画的时间
     * @returns {{enterAnimationTime: number, exitAnimationTime: number}}
     */
    getAnimationTime() {
        return {
            enterAnimationTime: this._enterAnimationTime,
            loopAnimationTime: this._loopAnimationTime,
            exitAnimationTime: this._exitAnimationTime,
        };
    }

    /**
     * @desc 适配动画时长
     */
    fixAnimationTime(updateAnimation = false) {
        const { animationInName, animationOutName } = this.getAnimationName();

        // 如果没有动画，则不做处理
        if (animationInName === 'None' && animationOutName === 'None') return;

        // 循环动画不做处理
        const isLoop = Animation.isLoopAnimation(animationInName);
        if (isLoop) return;

        const { duration, delay, endDelay } = this.getTimeData(true);
        let { enterAnimationTime = 0, exitAnimationTime = 0 } = this.getAnimationTime();
        const currentDuration = FJMathUtil.handleNumberPrecision(duration - endDelay - delay);
        let d1 = animationInName !== 'None' ? enterAnimationTime : 0,
            d2 = animationOutName !== 'None' ? exitAnimationTime : 0;
        let maxDuration = Math.min(currentDuration, FJCoreConfig.maxAnimationDuration);

        if (d1 + d2 > maxDuration) {
            if (d1 !== 0 && d2 !== 0) {
                let offset = d1 + d2 - maxDuration;
                let offset1 = Math.min(d1 - FJCoreConfig.minAnimationDuration, offset / 2);
                let offset2 = Math.min(d2 - FJCoreConfig.minAnimationDuration, offset / 2);
                // 谁的偏移小，就优先改变谁
                if (offset1 <= offset2) {
                    d1 -= offset1;
                    d2 -= offset - offset1;
                } else {
                    d2 -= offset2;
                    d1 -= offset - offset2;
                }
            } else if (d1 !== 0) d1 = maxDuration;
            else if (d2 !== 0) d2 = maxDuration;
        }

        d1 = Math.round(d1 * 100) / 100;
        d2 = Math.round(d2 * 100) / 100;

        // 注意round
        if (d1 + d2 > maxDuration) {
            if (d1 < d2) {
                d2 = maxDuration - d1;
                d2 = Math.floor(d2 * 100) / 100;
            } else {
                d1 = maxDuration - d2;
                d1 = Math.floor(d1 * 100) / 100;
            }
        }

        if (enterAnimationTime !== d1 || exitAnimationTime !== d2) {
            this.setAnimationTime({
                enterAnimationTime: d1,
                exitAnimationTime: d2,
            });
        } else {
            if (updateAnimation) {
                this._createAnimation();

                // 考虑是否需要
                this.goToCoverFrame();
                this.updateProjectData();
            }
        }
    }

    clear() {
        this._destroyAnimation();
        this._destroyCustomAnimation();
        super.clear();
    }

    getCustomAnimationTarget() {
        return this._customAnimationTarget;
    }

    _isValidMoveAnimationData() {
        return this.customAnimationInfo && this.customAnimationInfo.cacheId;
    }
    /**
     * @desc 销毁动画
     */
    _destroyCustomAnimation() {
        if (this._customAnimation) {
            this._customAnimation = null;
        }
    }
    is3DAnimation() {
        const { animationLoopName, animationInName, animationOutName } = this.getAnimationName();

        return (
            FJMotionUtil.is3DAnimation(animationLoopName) ||
            FJMotionUtil.is3DAnimation(animationInName) ||
            FJMotionUtil.is3DAnimation(animationOutName)
        );
    }

    // 判断当前时间是否在3D动画中
    _judgeCurrentTimeIsInAnimation(time) {
        const { showDuration, hideDuration, loopDuration } = this.getDetailTime();
        let animateName = 'None';

        if (time <= showDuration + this._delay) {
            animateName = this._animationInName;
        } else if (time <= showDuration + loopDuration + this._delay) {
            animateName = this._animationLoopName;
        } else if (time <= this._duration - this._endDelay && time >= this._duration - this._endDelay - hideDuration) {
            animateName = this._animationOutName;
        }

        return FJMotionUtil.is3DAnimation(animateName);
    }

    /**
     * @desc 判断是否应该应用3D 动画的外扩效果
     * @param {number} time - 当前时间
     * @return {boolean}
     * @private
     */
    _shouldApply3DAnimationExpand(time) {
        if (!this.is3DAnimation()) {
            return false;
        }

        // TODO： 部分动画属性会在 animejs 中被其他动画继承，导致动画效果不正确，目前发现的perspective属性会有影响
        if (!this._judgeCurrentTimeIsInAnimation(time)) {
            const animeTag = this._animationTarget();
            animeTag.style.cssText = FJMotionUtil.removeTransformFunctions(animeTag.style.cssText, 'perspective');
            return false;
        }

        return true;
    }

    /**
     * @desc 计算3D 动画的外扩尺寸
     * @param {Object} rect - 容器矩形 {width, height}
     * @return {Object|null} 外扩尺寸 {horizontal, vertical} 或 null
     * @private
     */
    _calculate3DAnimationExpandSize(rect) {
        const expandSizeRatio = FJArchiveUtil.getExpandSizeRatio(this);

        if (!isObject(expandSizeRatio)) {
            return null;
        }

        return {
            horizontal: expandSizeRatio.horizontal * rect.width,
            vertical: expandSizeRatio.vertical * rect.height,
        };
    }
}

export default FJAnimationSprite;
